<script setup lang="ts">
/**
 * Knop of link met knoplook.
 *
 *   <VButton variant="secondary" size="sm" :icon="IconPlus">Toevoegen</VButton>
 *   <VButton variant="icon" :icon="IconTrash" :label="$t('global.delete')" />
 *
 * - variant: primary | secondary | outline-danger | danger | success | warning | icon
 *   | underline | info | link | text. Eén secundaire knop: een witte "outline" naast secondary is bewust
 *   geschrapt (keuze klant, niet consistent). icon/underline/info houden bewust hun
 *   huidige look (keuze klant 24/09: "Later opnieuw vragen" moet onopvallend blijven).
 * - size: md (standaard, de bestaande look) | sm | xs. Vroeger bestond er geen size en
 *   werd elke kleinere knop met `!p-0`/`!px-2 !py-1` uit elkaar getrokken.
 * - icon zonder tekst = iconknop; `label` is dan verplicht als toegankelijke naam (±140
 *   iconknoppen hadden er geen). Bij variant icon: 20×20 zonder padding, zoals de
 *   bestaande iconknoppen met `!p-0`.
 * - href: Inertia-Link; type download/external: gewone <a>.
 *
 * icon/underline/info zetten bewust óók de kale klasse (`icon`,
 * `info`, `underline`): de globale regels `.icon` (w-5 h-5, hover primary), `.info` en
 * Tailwinds `underline` hangen daaraan, dus zonder die klasse verandert hun look.
 *
 * - link: tekstlink in de primary-kleur, zonder padding, erft de lettergrootte van de
 *   tekst eromheen. Vervangt ButtonKind.TERTIARY en LINK (keuze klant 25/09/2026): die
 *   hadden geen eigen CSS en toonden een kale, vette hoofdletterknop die de
 *   aanroepers met !-klassen terugplooiden. Daarmee is ook de oude `kind`-prop weg.
 * - text: klikbare tekst die eruitziet als de tekst eromheen (kleur, gewicht, grootte
 *   van de ouder), links uitgelijnd, zonder padding, hover = primary. Voor een naam in
 *   een tabel die een voorbeeld opent, een map in een lijst of een aan/uit-label met
 *   eigen kleur (kleur op de ouder zetten). Afkappen: tekst in een
 *   `<span class="truncate text-inherit text-sm">` (een kale span krijgt
 *   via global.scss een vaste kleur en text-xs) en `max-w-full` op de knop.
 */
import { Link } from '@inertiajs/vue3'
import { type Component, computed, useSlots } from 'vue'
import { ButtonType, LinkTarget, Method } from '@/BaseEnums'
import VSpinner from './VSpinner.vue'

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'outline-danger'
    | 'danger'
    | 'success'
    | 'warning'
    | 'icon'
    | 'underline'
    | 'info'
    | 'link'
    | 'text'

export type ButtonSize = 'xs' | 'sm' | 'md'

const props = withDefaults(
    defineProps<{
        variant?: ButtonVariant
        size?: ButtonSize
        type?: ButtonType
        loading?: boolean
        disabled?: boolean
        href?: string | null
        method?: Method
        target?: LinkTarget | string
        /** Tabler-icoon (component) vóór de tekst. */
        icon?: Component | null
        /** Toegankelijke naam (aria-label + tooltip); verplicht bij een iconknop. */
        label?: string | null
        /** Volle breedte. */
        block?: boolean
    }>(),
    {
        variant: 'primary',
        size: 'md',
        type: ButtonType.BUTTON,
        loading: false,
        disabled: false,
        href: null,
        method: Method.GET,
        target: LinkTarget.SELF,
        icon: null,
        label: null,
        block: false,
    }
)

const slots = useSlots()

const iconOnly = computed(() => !!props.icon && !slots.default)

if (import.meta.env.DEV && props.icon && !slots.default && !props.label) {
    console.warn('[VButton] iconknop zonder label: geef `label` mee als toegankelijke naam.')
}

/** Varianten waarvan de kale klasse globale regels draagt (zie boven). */
const BARE_CLASS_VARIANTS: ButtonVariant[] = ['icon', 'underline', 'info']

const classes = computed(() => [
    'v-btn btn transition',
    BARE_CLASS_VARIANTS.includes(props.variant)
        ? `btn-${props.variant} ${props.variant}`
        : `btn-${props.variant}`,
    props.size !== 'md' && `v-btn--${props.size}`,
    iconOnly.value && 'v-btn--icon-only',
    props.block && 'w-full',
])

const iconClass = computed(() =>
    props.variant === 'icon' ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0'
)

const inactive = computed(() => props.disabled || props.loading)

const isAnchor = computed(
    () =>
        props.href != null &&
        (props.type === ButtonType.DOWNLOAD || props.type === ButtonType.EXTERNAL)
)

/**
 * Een uitgeschakelde link bestaat niet in HTML: vroeger bleef een `disabled`/`loading`
 * VButton met href gewoon klikbaar. aria-disabled + geen tabstop + pointer-events-none
 * maken hem echt inert.
 */
const linkAttrs = computed(() =>
    inactive.value ? { 'aria-disabled': true, tabindex: -1, class: 'pointer-events-none' } : {}
)

/**
 * LINK/DOWNLOAD/EXTERNAL zonder href belandden vroeger letterlijk als type="link" op de
 * <button>. Een ongeldig type is voor de browser "submit": zo'n knop in een formulier
 * verstuurde het. Enkel submit/reset zijn echte knoptypes, de rest wordt "button".
 */
const buttonType = computed<'button' | 'submit' | 'reset'>(() =>
    props.type === ButtonType.SUBMIT || props.type === ButtonType.RESET ? props.type : 'button'
)

const a11y = computed(() => ({
    'aria-label': props.label ?? undefined,
    // Ook bij variant icon met het icoon in de slot (zo staan de actiebalk-knoppen);
    // anders gaf enkel de :icon-prop een tooltip en bleven die icoontjes naamloos.
    title: iconOnly.value || props.variant === 'icon' ? (props.label ?? undefined) : undefined,
    'aria-busy': props.loading || undefined,
}))
</script>

<template>
    <a
        v-if="isAnchor"
        :href="href!"
        :target="target"
        :class="classes"
        v-bind="{ ...a11y, ...linkAttrs }"
    >
        <VSpinner v-if="loading" size="xs" tone="current" />
        <component :is="icon" v-else-if="icon" :class="iconClass" aria-hidden="true" />
        <slot />
    </a>
    <Link
        v-else-if="href != null"
        :href="href"
        :method="method"
        :class="classes"
        v-bind="{ ...a11y, ...linkAttrs }"
        preserve-state
        preserve-scroll
    >
        <VSpinner v-if="loading" size="xs" tone="current" />
        <component :is="icon" v-else-if="icon" :class="iconClass" aria-hidden="true" />
        <slot />
    </Link>
    <button v-else :type="buttonType" :disabled="inactive" :class="classes" v-bind="a11y">
        <VSpinner v-if="loading" size="xs" tone="current" />
        <component :is="icon" v-else-if="icon" :class="iconClass" aria-hidden="true" />
        <slot />
    </button>
</template>
